/**
 * ListingCard component
 *
 * Displays a single listing in a card format with key information
 * and interactive features.
 */

import React from "react";
import { MapPin, Train, Maximize, Building, Calendar } from "lucide-react";
import { useTranslation } from "../../hooks/useTranslation";
import type { Listing } from "../../types/listings";

interface ListingCardProps {
  listing: Listing;
  onClick?: (listing: Listing) => void;
  isSelected?: boolean;
  onHover?: (listing: Listing | null) => void;
}

/**
 * Format price as JPY
 */
function formatPrice(price: number): string {
  return new Intl.NumberFormat("ja-JP", {
    style: "currency",
    currency: "JPY",
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Get placeholder image URL
 */
function getPlaceholderImage(_index: number = 0): string {
  return "https://via.placeholder.com/300x200/e5e7eb/9ca3af?text=No+Image";
}

export const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  onClick,
  isSelected = false,
  onHover,
}) => {
  const t = useTranslation();

  const handleClick = () => {
    onClick?.(listing);
  };

  const handleMouseEnter = () => {
    onHover?.(listing);
  };

  const handleMouseLeave = () => {
    onHover?.(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.(listing);
    }
  };

  // Get thumbnail or first image or placeholder
  const imageUrl =
    listing.thumbnail || listing.images[0] || getPlaceholderImage();

  // Format location string
  const locationParts = [listing.ward, listing.city, listing.prefecture].filter(
    Boolean,
  );
  const locationString = locationParts.join(", ");

  // Format station info
  const stationInfo = listing.station
    ? `${listing.station}${listing.walkMinutes ? ` (${listing.walkMinutes} ${t("listings.min")})` : ""}`
    : null;

  // Get key amenity badges
  const keyAmenities = [];
  if (listing.amenities.foreignerFriendly) {
    keyAmenities.push({
      key: "foreigner",
      label: t("listings.foreignerFriendly"),
    });
  }
  if (listing.amenities.petsAllowed) {
    keyAmenities.push({ key: "pets", label: t("listings.petsAllowed") });
  }
  if (listing.amenities.airConditioning) {
    keyAmenities.push({ key: "ac", label: t("listings.airConditioning") });
  }

  // Calculate building age
  const buildingAge = listing.yearBuilt
    ? new Date().getFullYear() - listing.yearBuilt
    : null;

  return (
    <article
      className={`
        bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer
        transition-all duration-200 hover:shadow-md hover:border-gray-300
        ${isSelected ? "ring-2 ring-primary-500 border-primary-500" : ""}
      `}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${listing.title} - ${formatPrice(listing.price)}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={imageUrl}
          alt={listing.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {/* Room layout badge */}
        <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium">
          {listing.roomLayout}
        </div>
      </div>

      {/* Content - all inside the card border */}
      <div className="p-3 border-t border-gray-100">
        {/* Price */}
        <div className="mb-2">
          <span className="text-lg font-bold text-gray-900">
            {formatPrice(listing.price)}
          </span>
          <span className="text-xs text-gray-500 ml-1">
            /{t("listings.month")}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-medium text-gray-800 line-clamp-1 text-sm mb-2">
          {listing.title}
        </h3>

        {/* Location with icon */}
        <div className="flex items-center gap-1.5 text-gray-600 text-xs mb-1">
          <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <span className="line-clamp-1">{locationString}</span>
        </div>

        {/* Station with icon */}
        {stationInfo && (
          <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-2">
            <Train className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span>{stationInfo}</span>
          </div>
        )}

        {/* Stats row with icons */}
        <div className="flex items-center gap-3 text-xs text-gray-600 mb-2 pt-2 border-t border-gray-100">
          <span className="flex items-center gap-1">
            <Maximize className="w-3 h-3 text-gray-400" />
            {listing.areaSqm} m²
          </span>
          {listing.floor && (
            <span className="flex items-center gap-1">
              <Building className="w-3 h-3 text-gray-400" />
              {listing.floor}F
            </span>
          )}
          {buildingAge !== null && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3 text-gray-400" />
              {buildingAge}y
            </span>
          )}
        </div>

        {/* Deposit/Key money */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
          <span>
            {t("listings.deposit")}:{" "}
            {listing.deposit === 0
              ? "0"
              : listing.deposit
                ? `${Math.round(listing.deposit / listing.price)}M`
                : "-"}
          </span>
          <span className="text-gray-300">•</span>
          <span>
            {t("listings.keyMoney")}:{" "}
            {listing.keyMoney === 0
              ? "0"
              : listing.keyMoney
                ? `${Math.round(listing.keyMoney / listing.price)}M`
                : "-"}
          </span>
        </div>

        {/* Amenity badges */}
        {keyAmenities.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
            {keyAmenities.slice(0, 3).map((amenity) => (
              <span
                key={amenity.key}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
              >
                {amenity.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export default ListingCard;
